// mulberry32: tiny, deterministic, good enough for V0.x. State is a single uint32
// stored as a number; it round-trips through JSON cleanly.

export type RngState = { seed: number; cursor: number };

export function createRng(seed: number): RngState {
  return { seed: seed >>> 0, cursor: seed >>> 0 };
}

export function nextFloat(rng: RngState): number {
  rng.cursor = (rng.cursor + 0x6d2b79f5) >>> 0;
  let t = rng.cursor;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function nextInt(rng: RngState, maxExclusive: number): number {
  return Math.floor(nextFloat(rng) * maxExclusive);
}

// Stateless, deterministic 32-bit hash. Mixes three numbers into a uint32 via
// mulberry32-style integer ops. Does NOT mutate state — callers can hash
// (state.seed, state.tick, anyContext) safely on the bot side without
// violating Rule 1 (sim owns mutation). The `| 0` coercions matter because
// EntityId-like brands can exceed 2^31; without them Math.imul would silently
// truncate inconsistently.
export function stableHash(a: number, b: number, c: number): number {
  let x = ((a | 0) ^ Math.imul(b | 0, 0x9e3779b1) ^ Math.imul(c | 0, 0x85ebca6b)) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d) >>> 0;
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

// Stateless float in [0, 1). Same shape as `nextFloat` but does not mutate.
export function stableFloat(a: number, b: number, c: number): number {
  return stableHash(a, b, c) / 0x1_0000_0000;
}
